// Mapbox token will be imported dynamically where needed
import type { LocationCoordinates } from './location';
import { getCurrentLocation } from './location';
import { AppState } from 'react-native';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { getCountryCodeFromDestination, haversineDistance } from '@/utils/geocodeHelpers';
import { isAggressiveMapboxCostMode } from '@/config/mapboxCostPolicy';
import { functions, httpsCallableWithTimeout } from '@/lib/functions';
import { getBaselineLandmarksForCity } from '@/data/baselineLandmarks';
import { trackSanitizationIntercept } from '@/services/sanitizationTelemetry';

export type PlaceCategory = 'restaurant' | 'museum' | 'attraction' | 'viewpoint' | 'landmark';

/**
 * Search Box `/category/{id}` canonical IDs (see Mapbox `/list/category`).
 * Prefer stable IDs from docs/examples; unknown IDs no-op with empty response.
 */
const SEARCH_BOX_CATEGORY_IDS: Record<PlaceCategory, string[]> = {
  restaurant: ['restaurant', 'cafe', 'coffee'],
  museum: ['museum'],
  attraction: ['zoo', 'aquarium', 'park', 'amusement_park', 'theater'],
  viewpoint: ['park'],
  landmark: ['monument', 'museum', 'castle', 'palace', 'bridge'],
};

export function formatPlaceCategoryLabel(category?: PlaceCategory | null): string {
  switch (category) {
    case 'restaurant':
      return 'Restaurant';
    case 'museum':
      return 'Museum';
    case 'attraction':
      return 'Attraction';
    case 'viewpoint':
      return 'Viewpoint';
    case 'landmark':
      return 'Landmark';
    default:
      return 'Suggested place';
  }
}

export type Place = {
  id: string;
  name: string;
  category: PlaceCategory;
  coordinates: LocationCoordinates;
  address?: string;
  rating?: number;
  description?: string;
  imageUrl?: string;
  tags?: string[];
  popularityScore?: number;
  trendingReason?: string;
  isMustSee?: boolean;
};

const CACHE_FRESH_MS = 180 * 24 * 60 * 60 * 1000; // 6 months
const CACHE_HARD_TTL_MS = 2 * 365 * 24 * 60 * 60 * 1000; // 2 years
const PREWARM_LOCAL_COOLDOWN_MS = 20 * 60 * 1000;

/** Street-type tokens (subdivisions); not venue names like "The Landmark". */
const STREET_TYPE_TOKEN =
  /\b(lane|ln\.?|way|walk|terrace|ter\b|trl\.?|trail|loop|cove|run|xing|crossing|road|rd\.?|street|st\.?|avenue|ave\.?|drive|dr\.?|boulevard|blvd\.?|court|ct\.?|circle|cir\.?|place|pl\.?|parkway|pkwy|highway|hwy)\b/i;

/**
 * Marketing block / query-bait names; with a street suffix they are almost never real POIs
 * (e.g. "Landmark Lane", "Monument Walk", "Viewpoint Road").
 */
const MISLEADING_BLOCK_NAME_PREFIX = /^(landmark|monument)\b/i;
const VIEWPOINT_STREET_PREFIX = /^(viewpoint|view\s+point)\b/i;

/** True when the label looks like a bare street/road, not a named venue or landmark. */
export function isGenericStreetLikeLabel(name: string): boolean {
  const t = name.trim();
  if (!t || t.length < 3) return true;
  const lower = t.toLowerCase();
  if (/\b(highway|hwy|route\s+\d+|interstate|i-\d+)\b/i.test(t)) return true;
  // JFK-style facility addresses, not a named attraction.
  if (/^building\s+\d+\b/i.test(t)) return true;
  if (/^(north|south|east|west)\s+\w+\s+(street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?)\b/i.test(t)) {
    return true;
  }
  if (MISLEADING_BLOCK_NAME_PREFIX.test(t) && STREET_TYPE_TOKEN.test(t)) {
    return true;
  }
  if (VIEWPOINT_STREET_PREFIX.test(t) && STREET_TYPE_TOKEN.test(t)) {
    return true;
  }
  if (/^\d{1,4}(?:st|nd|rd|th)?\s+/.test(t)) {
    if (
      /\b(street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|drive|dr\.?|lane|ln\.?|place|pl\.?|court|ct\.?|way|terrace|pkwy|parkway)\b/i.test(
        lower,
      )
    ) {
      return true;
    }
  }
  // Query "historic site" often returns subdivision roads.
  if (/^historic\s+/i.test(t) && STREET_TYPE_TOKEN.test(t)) {
    return true;
  }
  return false;
}

/** Reject 1–3 character noise; allow short real names (e.g. MoMA). */
const MIN_LANDMARK_NAME_LEN = 4;

/**
 * Extra filters for Mapbox discovery results (beyond street-shaped names).
 */
export function isJunkDiscoveryPoi(
  displayName: string,
  _fullPlaceName: string,
  placeTypes?: string[],
  relevance?: number,
): boolean {
  const name = displayName.trim();
  const hasNonAscii = /[^\x00-\x7F]/.test(name);
  // Keep short non-Latin venue names (common in JP/CN/KR) while still filtering
  // ultra-short ASCII noise like "St", "Rd", "NW".
  const minLen = hasNonAscii ? 2 : MIN_LANDMARK_NAME_LEN;
  if (name.length > 0 && name.length < minLen) {
    return true;
  }
  if (isGenericStreetLikeLabel(name)) {
    return true;
  }
  // Geocoding v5 often omits relevance or returns modest scores; Search Box uses synthetic scores.
  if (relevance !== undefined && relevance < 0.12) {
    return true;
  }
  if (placeTypes?.length) {
    const poiish = placeTypes.some((t) => t === 'poi' || (typeof t === 'string' && t.startsWith('poi')));
    if (!poiish) {
      return true;
    }
  }
  if (/\b(apartments?|apt\.?|condos?|condominiums?|townhomes?|townhouses?)\b/i.test(name)) {
    return true;
  }
  if (/^(lot|unit|suite|bldg|blk|block)\s+/i.test(name)) {
    return true;
  }
  return false;
}

function discoveryPopularityBoost(displayName: string, poiCategory?: string): number {
  let boost = 0;
  const cat = (poiCategory || '').toLowerCase();
  if (
    cat &&
    /museum|gallery|monument|historic|landmark|attraction|park|garden|zoo|aquarium|castle|palace|memorial|place_of_worship|religious|theatre|theater|stadium|arena|opera|library|embassy/.test(
      cat,
    )
  ) {
    boost += 22;
  }
  if (
    /\b(museum|galler(?:y|ies)|cathedral|basilica|mosque|synagogue|temple|palace|castle|memorial|monument|tower|park|garden|zoo|aquarium|opera|stadium|arena|observatory|library|fort\b|plaza)\b/i.test(
      displayName,
    )
  ) {
    boost += 14;
  }
  return boost;
}

function venueLabelScore(label: string): number {
  let s = Math.min(label.length, 80);
  if (/\b(museum|galler(?:y|ies)|cathedral|basilica|memorial|monument|palace|castle|park|garden|zoo|aquarium|tower|observatory|library|plaza|arena|stadium|opera)\b/i.test(label)) {
    s += 45;
  }
  return s;
}

function pickLandmarkDisplayLabel(
  featureText: string | undefined,
  placeName: string,
): { label: string; isLowQuality: boolean } {
  const full = (placeName || '').trim();
  const ft = (featureText || '').trim();
  const segments = full.split(',').map((s) => s.trim()).filter(Boolean);
  const ordered = [ft, segments[0], full].filter(Boolean) as string[];
  const candidates = [...new Set(ordered)];

  const good = candidates.filter((c) => !isGenericStreetLikeLabel(c));
  if (good.length === 0) {
    return { label: candidates[0] || full, isLowQuality: true };
  }
  good.sort((a, b) => venueLabelScore(b) - venueLabelScore(a));
  return { label: good[0]!, isLowQuality: false };
}

type CachedPlaces = {
  city: string;
  places: Place[];
  cachedAt: Timestamp | Date;
  /**
   * Schema version for cache entries. Bump whenever landmark-fetch logic changes
   * in a way that could poison previously-cached results (e.g. new distance cap,
   * new fallback chain, new display labels). Entries without this field or with
   * a lower version are treated as stale misses.
   */
  schemaVersion?: number;
  cacheKeyVersion?: number;
  countryCode?: string | null;
  language?: string | null;
};

/**
 * Current landmark cache schema version.
 *   v2 (2026-04): tilequery fallback + 30km landmark distance cap from proximity.
 *                 Invalidates v1 entries that may contain distant outliers (e.g. Paracas
 *                 Reserve in a "Lima" query).
 *   v3 (2026-04): tilequery always runs and merges (not gated on allPlaces===0),
 *                 plus an unfiltered-class retry when curated classes yield <6.
 *                 Invalidates v2 entries that cached single-landmark results from
 *                 the gated flow (observed for Tokyo/Lima).
 *   v4 (2026-04): tilequery sampled at a 5-point ring (center + N/E/S/W at ~3km).
 *                 Invalidates v3 entries that cached landmarks clustered within a
 *                 500m radius of a single proximity point (observed for Tokyo/Lima).
 *   v5 (2026-04): corrected Tokyo static coords (was Setagaya, now Tokyo Station) +
 *                 added Lima/Cusco static coords + blocked streets-v8 `education`,
 *                 `food_and_drink`, `lodging`, etc. classes that were surfacing
 *                 elementary schools and shops as "landmarks".
 */
const LANDMARK_CACHE_SCHEMA_VERSION = 5;
const LANDMARK_CACHE_KEY_VERSION = 2;

const localPrewarmAttemptByKey = new Map<string, number>();
const inFlightBackgroundRefreshes = new Map<string, Promise<void>>();

type LandmarkCacheContext = {
  countryCode?: string;
  language?: string;
};

type PrewarmLandmarkCacheResponse = {
  accepted: boolean;
  shouldRefresh: boolean;
  lockUntilMs?: number;
  cacheKey: string;
};

const normalizeCountryCode = (value?: string | null): string => {
  if (value && /^[a-z]{2}$/i.test(value.trim())) {
    return value.trim().toUpperCase();
  }
  return 'XX';
};

const normalizeLanguageCode = (value?: string | null): string => {
  if (value && /^[a-z]{2,10}$/i.test(value.trim())) {
    return value.trim().toLowerCase();
  }
  return 'en';
};

const normalizeCityKeyPart = (city: string): string =>
  city.trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^\w, -]/g, '');

const buildCityCacheKey = (city: string, context?: LandmarkCacheContext): string => {
  const normalizedCity = normalizeCityKeyPart(city).replace(/\s+/g, '_').replace(/,+/g, '_');
  const country = normalizeCountryCode(context?.countryCode);
  const language = normalizeLanguageCode(context?.language);
  return `v${LANDMARK_CACHE_KEY_VERSION}_${normalizedCity}_${country}_${language}`;
};

const buildLegacyCityCacheKey = (city: string): string =>
  city.toLowerCase().replace(/\s+/g, '_');

const requestLandmarkCachePrewarmCallable = httpsCallableWithTimeout<
  {
    city: string;
    country?: string;
    language?: string;
  },
  PrewarmLandmarkCacheResponse
>(functions, 'requestLandmarkCachePrewarm', 15_000);

export const requestLandmarkCachePrewarm = async (
  city: string,
  context?: LandmarkCacheContext,
): Promise<PrewarmLandmarkCacheResponse | null> => {
  try {
    const response = await requestLandmarkCachePrewarmCallable({
      city,
      country: context?.countryCode ? normalizeCountryCode(context.countryCode) : undefined,
      language: context?.language ? normalizeLanguageCode(context.language) : undefined,
    });
    return response.data ?? null;
  } catch (error) {
    if (__DEV__) {
      console.warn('[Places] requestLandmarkCachePrewarm failed', { city, error });
    }
    return null;
  }
};

const triggerBackgroundLandmarkRefresh = (
  city: string,
  options?: {
    categories?: PlaceCategory[];
    proximity?: LocationCoordinates;
    limit?: number;
    country?: string;
    language?: string;
  },
): void => {
  const context: LandmarkCacheContext = {
    countryCode: options?.country || getCountryCodeFromDestination(city),
    language: options?.language,
  };
  const cacheKey = buildCityCacheKey(city, context);
  const now = Date.now();
  const lastAttempt = localPrewarmAttemptByKey.get(cacheKey) ?? 0;
  if (now - lastAttempt < PREWARM_LOCAL_COOLDOWN_MS) {
    return;
  }
  localPrewarmAttemptByKey.set(cacheKey, now);
  if (inFlightBackgroundRefreshes.has(cacheKey)) {
    return;
  }

  const task = (async () => {
    const prewarmResult = await requestLandmarkCachePrewarm(city, context);
    if (!prewarmResult?.shouldRefresh) {
      return;
    }
    await fetchLandmarks(city, {
      ...(options || {}),
      forceRefresh: true,
      backgroundRefresh: true,
      country: context.countryCode,
      language: context.language,
    });
  })()
    .catch((error) => {
      if (__DEV__) {
        console.warn('[Places] background landmark refresh failed', { city, error });
      }
    })
    .finally(() => {
      inFlightBackgroundRefreshes.delete(cacheKey);
    });

  inFlightBackgroundRefreshes.set(cacheKey, task);
};

/**
 * Fetch landmarks and trendy places using Mapbox Places API
 * Note: Mapbox Places API may require a specific plan. This uses the Geocoding API
 * which can search for POIs (Points of Interest)
 */
export const fetchLandmarks = async (
  city: string,
  options?: {
    categories?: PlaceCategory[];
    proximity?: LocationCoordinates;
    limit?: number;
    country?: string; // ISO-3166 alpha-2
    language?: string; // IETF language tag
    debug?: boolean; // Force verbose logging even outside __DEV__
    forceRefresh?: boolean;
    backgroundRefresh?: boolean;
  }
): Promise<Place[]> => {
  const debug = !!options?.debug;
  const aggressiveCostMode = isAggressiveMapboxCostMode();
  const countryCodeRaw = options?.country || getCountryCodeFromDestination(city);
  const countryCode = countryCodeRaw ? countryCodeRaw.toUpperCase() : undefined;
  const language = normalizeLanguageCode(options?.language);
  const cacheContext: LandmarkCacheContext = {
    countryCode,
    language,
  };
  const requestedLimit = Math.max(8, options?.limit || 20);

  try {
    // Stop discovery once we have enough candidates to produce a stable on-map subset.
    const earlyStopTarget = aggressiveCostMode
      ? Math.max(6, Math.min(requestedLimit, 12))
      : Math.max(12, Math.min(requestedLimit + 8, 40));

    // Check shared cache first (fresh-first, stale-while-revalidate).
    const cached = await getCachedPlaces(city, { allowExpired: true }, cacheContext);
    if (cached && !options?.forceRefresh) {
      const rawCachedAt = cached.cachedAt as Date | { toDate?: () => Date } | string | number;
      const cachedAt =
        rawCachedAt instanceof Date
          ? rawCachedAt
          : rawCachedAt && typeof rawCachedAt === 'object' && typeof rawCachedAt.toDate === 'function'
            ? rawCachedAt.toDate()
            : new Date(rawCachedAt as string | number);
      const cacheIsCurrentSchema =
        (cached.schemaVersion ?? 0) >= LANDMARK_CACHE_SCHEMA_VERSION;
      const cacheIsCurrentKeyVersion =
        (cached.cacheKeyVersion ?? 0) >= LANDMARK_CACHE_KEY_VERSION;
      const cacheAgeMs = Date.now() - cachedAt.getTime();
      // Treat "fresh but empty" cache as a miss; otherwise a failed refresh can hide landmarks for days.
      // Also treat older-schema entries as misses so invalidation happens cleanly on upgrade.
      if (
        cacheIsCurrentSchema &&
        cacheIsCurrentKeyVersion &&
        cached.places?.length > 0 &&
        cacheAgeMs < CACHE_FRESH_MS
      ) {
        return ensurePlaceMetadata(cached.places);
      }
      if (
        cacheIsCurrentSchema &&
        cacheIsCurrentKeyVersion &&
        cached.places?.length > 0 &&
        cacheAgeMs < CACHE_HARD_TTL_MS
      ) {
        if (!options?.backgroundRefresh) {
          triggerBackgroundLandmarkRefresh(city, {
            categories: options?.categories,
            proximity: options?.proximity,
            limit: options?.limit,
            country: countryCode,
            language,
          });
        }
        return ensurePlaceMetadata(cached.places);
      }
      if (__DEV__ && !cacheIsCurrentSchema) {
        console.warn('[Places] Invalidating old-schema landmark cache', {
          city,
          cachedVersion: cached.schemaVersion ?? 0,
          currentVersion: LANDMARK_CACHE_SCHEMA_VERSION,
        });
      }
    }

    // Use the same Mapbox search path used elsewhere in the app (better params + error handling).
    const { searchPlaces, searchBoxCategoryNearby, searchBoxForwardNearby, tilequeryPoiNearby } =
      await import('./maps');
    const categories = options?.categories || ['restaurant', 'museum', 'attraction', 'viewpoint', 'landmark'];
    const countryCodeFilter = countryCode ? countryCode.toLowerCase() : countryCode;
    
    // Mapbox doesn't support category filters via this endpoint; we approximate via queries.
    // Include a few "must-see" style synonyms to increase recall.
    const searchTermsForCategory = (cat: PlaceCategory): string[] => {
      switch (cat) {
        case 'restaurant':
          return ['restaurant', 'best restaurants', 'cafe'];
        case 'museum':
          return ['museum', 'art museum', 'history museum'];
        case 'attraction':
          return [
            'tourist attraction',
            'historic site',
            'observation deck',
            'national memorial',
            'art gallery',
            'public park',
            'botanical garden',
            'historic building',
            'aquarium',
            'zoo',
            'planetarium',
            'visitor center',
          ];
        case 'viewpoint':
          return ['scenic overlook', 'scenic viewpoint', 'lookout point', 'panorama'];
        case 'landmark':
          return [
            'famous landmark',
            'historic landmark',
            'iconic architecture',
            'notable building',
            'cathedral',
            'basilica',
            'memorial',
            'national monument',
            'palace',
            'castle',
            'observatory',
          ];
        default:
          return [];
      }
    };

    const allPlaces: Place[] = [];
    const baselineFallbackPlaces = (): Place[] => {
      const seeds = getBaselineLandmarksForCity(city, {
        countryCode,
        limit: Math.min(requestedLimit, 15),
      });
      if (!seeds.length) return [];

      const toDistanceKm = (coords: LocationCoordinates): number => {
        if (!options?.proximity) return Number.POSITIVE_INFINITY;
        return haversineKm(
          options.proximity.latitude,
          options.proximity.longitude,
          coords.latitude,
          coords.longitude,
        );
      };

      const places = seeds
        .map((seed, index) => ({
          id: `baseline-${city.replace(/\s+/g, '-').toLowerCase()}-${index}`,
          name: seed.name,
          category: 'landmark' as const,
          coordinates: seed.coordinates,
          address: seed.address,
          rating: undefined,
          description: undefined,
          popularityScore: Math.max(40, 100 - index * 6),
          tags: ['baseline-seeded'],
        }))
        .sort((a, b) => toDistanceKm(a.coordinates) - toDistanceKm(b.coordinates));

      return places.slice(0, requestedLimit);
    };

    type GeocodeRow = {
      coordinates: [number, number];
      placeName: string;
      featureText?: string;
      poiCategory?: string;
      placeTypes?: string[];
      relevance?: number;
    };

    // Cap landmarks to within ~30km of the city proximity so English term fallbacks can't drag
    // in distant same-country tourist attractions (e.g. Paracas Reserve appearing in a "Lima" query).
    const LANDMARK_MAX_DISTANCE_KM = 30;
    const proximityForCap = options?.proximity;
    const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      const R = 6371;
      const toRad = (v: number) => (v * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };
    const isTooFarFromProximity = (coords: [number, number]): boolean => {
      if (!proximityForCap) return false;
      const km = haversineKm(
        proximityForCap.latitude,
        proximityForCap.longitude,
        coords[1],
        coords[0],
      );
      return km > LANDMARK_MAX_DISTANCE_KM;
    };

    const appendFromSearchBoxRows = (rows: GeocodeRow[], placeCategory: PlaceCategory, idPrefix: string) => {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i]!;
        if (isTooFarFromProximity(r.coordinates)) {
          continue;
        }
        const { label, isLowQuality } = pickLandmarkDisplayLabel(r.featureText, r.placeName);
        if (isLowQuality || isJunkDiscoveryPoi(label, r.placeName, r.placeTypes, undefined)) {
          continue;
        }
        const popularityScore =
          calculatePopularityScore({
            rating: undefined,
            reviewCount: 0,
            relevance: r.relevance,
          }) + discoveryPopularityBoost(label, r.poiCategory);

        const place = {
          id: `${idPrefix}-${r.coordinates[0].toFixed(4)}-${r.coordinates[1].toFixed(4)}-${i}`,
          name: label,
          category: placeCategory,
          coordinates: { latitude: r.coordinates[1], longitude: r.coordinates[0] },
          address: r.placeName,
          rating: undefined,
          description: undefined,
          popularityScore,
          tags: [],
        } as Place;

        const dup = allPlaces.some(
          (p) =>
            p.coordinates.latitude === place.coordinates.latitude &&
            p.coordinates.longitude === place.coordinates.longitude,
        );
        if (!dup) allPlaces.push(place);
      }
    };

    const finalizePlaces = async (): Promise<Place[]> => {
      const normalizedPlaces = ensurePlaceMetadata(allPlaces);
      const limitedPlaces = limitPlacesByCategory(normalizedPlaces, options?.limit || 50);
      const enrichedPlaces = tagTrendingPlaces(limitedPlaces);

      if (enrichedPlaces.length > 0) {
        await cachePlaces(city, enrichedPlaces, cacheContext);
        return enrichedPlaces;
      }

      const baseline = baselineFallbackPlaces();
      if (baseline.length > 0) {
        if (__DEV__ || debug) {
          console.warn('[Places] fetchLandmarks using baseline fallback', {
            city,
            count: baseline.length,
            countryCode,
          });
        }
        // Intentionally not cached to avoid long-lived baseline replacing fresher live data.
        return baseline;
      }

      return [];
    };

    // Mapbox Search Box `/category` returns named POIs. Proximity is required; bbox can over-filter — try without first.
    if (options?.proximity) {
      const perRequestLimit = Math.min(8, Math.max(4, Math.ceil((options.limit || 20) / 8)));
      const MAX_SEARCH_BOX_CALLS = 3;
      let searchBoxCalls = 0;
      const shouldStopEarly = () => allPlaces.length >= earlyStopTarget;

      const runCategoryPass = async (
        bbox: string | undefined,
        opts?: { useCountry?: boolean },
      ) => {
        const useCountry = opts?.useCountry !== false;
        for (const placeCategory of categories) {
          if (searchBoxCalls >= MAX_SEARCH_BOX_CALLS || shouldStopEarly()) break;
          const ids = (SEARCH_BOX_CATEGORY_IDS[placeCategory] || []).slice(0, 1);
          for (const catId of ids) {
            if (searchBoxCalls >= MAX_SEARCH_BOX_CALLS || shouldStopEarly()) break;
            searchBoxCalls += 1;
            const rows = await searchBoxCategoryNearby(catId, {
              proximity: options.proximity!,
              limit: perRequestLimit,
              country: useCountry ? countryCodeFilter : undefined,
              bbox,
            });
            appendFromSearchBoxRows(
              rows,
              placeCategory,
              `sb${useCountry ? '' : '-nc'}-${catId}`,
            );
          }
        }
      };

      // 1. Primary: country-filtered, no bbox.
      await runCategoryPass(undefined);
      // 2. If empty, retry WITHOUT country filter. Observed for international locales (JP/PE/...)
      //    where country=<code> over-filters POIs that don't carry an explicit country property.
      //    This is the single biggest factor behind NY working but Tokyo/Lima returning 0.
      if (allPlaces.length === 0 && countryCodeFilter) {
        searchBoxCalls = 0;
        await runCategoryPass(undefined, { useCountry: false });
      }
      // In aggressive cost mode we skip additional bbox passes to avoid fan-out.

      // 5. Tilequery — Always run and merge (not gated on allPlaces===0). Search Box has
      //    US-centric category coverage, so even when it returns ~some~ results for a country
      //    we can still be starved of landmarks elsewhere (the observed "only 1 landmark"
      //    bug). Tilequery reads directly from the vector tile source the map renders from,
      //    so it has global coverage. Merging is idempotent via the coord-dedupe in
      //    `appendFromSearchBoxRows`.
      //
      //    NOTE: `poi_label.class` uses a fixed vocabulary from the streets-v8 reference —
      //    values like `arts_and_entertainment`, `park_like`, `historic`, `landmark`, `religion`.
      //    Using category names like `museum` / `park` / `zoo` silently drops everything.
      try {
        // Map streets-v8 class → our PlaceCategory enum.
        const classToCategory = (klass: string | undefined): PlaceCategory => {
          switch (klass) {
            case 'arts_and_entertainment':
              return 'museum';
            case 'park_like':
            case 'sport_and_leisure':
            case 'visitor_amenities':
              return 'attraction';
            case 'landmark':
            case 'historic':
            case 'religion':
            default:
              return 'landmark';
          }
        };

        // Classes we NEVER want as "landmarks" even when the unfiltered fallback pass runs.
        // streets-v8 bins schools under `education`, restaurants/cafes under `food_and_drink`,
        // hotels under `lodging`, convenience stores under `food_and_drink_stores`, etc.
        const BLOCKED_CLASSES = new Set([
          'education',
          'food_and_drink',
          'food_and_drink_stores',
          'lodging',
          'commercial_services',
          'medical',
          'motorist',
          'industrial',
          'general',
        ]);

        const absorbTileRows = (tileRows: Awaited<ReturnType<typeof tilequeryPoiNearby>>, tag: string) => {
          const byCategory = new Map<PlaceCategory, typeof tileRows>();
          let blocked = 0;
          for (const row of tileRows) {
            if (row.poiCategory && BLOCKED_CLASSES.has(row.poiCategory)) {
              blocked += 1;
              continue;
            }
            const cat = classToCategory(row.poiCategory);
            const bucket = byCategory.get(cat) || [];
            bucket.push(row);
            byCategory.set(cat, bucket);
          }
          for (const [cat, rows] of byCategory) {
            appendFromSearchBoxRows(rows, cat, `${tag}-${cat}`);
          }
          if (__DEV__ || debug) {
            console.warn('[Places] fetchLandmarks tilequery absorb', {
              tag,
              raw: tileRows.length,
              blockedByClass: blocked,
              kept: allPlaces.length,
              byClass: Array.from(byCategory.entries()).map(([c, r]) => [c, r.length]),
            });
          }
        };

        // Curated streets-v8 `poi_label.class` values that correspond to actual tourist-facing
        // landmarks. Intentionally excludes `education` (schools), `general` (low-signal),
        // `food_and_drink`, `lodging`, `commercial_services`, `medical`, `motorist`.
        const CURATED_CLASSES = [
          'landmark',
          'historic',
          'park_like',
          'religion',
          'arts_and_entertainment',
          'visitor_amenities',
          'sport_and_leisure',
        ];

        // Tilequery returns up to 50 features sorted by distance from proximity. In a dense
        // metro, those 50 routinely cluster within ~500m of the center point (observed with
        // Tokyo/Lima where landmarks piled on top of each other). To spread landmarks across
        // the city, sample tilequery at a ring of offset points in addition to the center.
        //
        // Offsets are ~3km (≈0.027°) N/E/S/W of proximity — far enough to surface distinct
        // clusters but still well inside the 30km downstream distance cap. Each call is
        // cheap (tile read) and merging is idempotent via the coord-dedupe in
        // `appendFromSearchBoxRows`.
        const samplingPoints: Array<{ lat: number; lng: number; tag: string }> = (() => {
          const centerLat = options.proximity!.latitude;
          // Approximate kilometers-to-degrees conversion around the current latitude.
          const latOffsetDeg = 3 / 111; // ~3km north/south
          const cosLat = Math.cos((centerLat * Math.PI) / 180);
          const safeCosLat = Math.max(Math.abs(cosLat), 0.2);
          const lngOffsetDeg = 3 / (111 * safeCosLat); // ~3km east/west

          return [
            { lat: 0, lng: 0, tag: 'center' },
            { lat: latOffsetDeg, lng: 0, tag: 'north' },
            { lat: -latOffsetDeg, lng: 0, tag: 'south' },
            { lat: 0, lng: lngOffsetDeg, tag: 'east' },
            { lat: 0, lng: -lngOffsetDeg, tag: 'west' },
          ];
        })();

        // 5a. Primary ring pass: curated class list at center + 4 cardinal offsets. Filters out
        //     food/shopping noise. Runs in parallel; failed calls return [] and are ignored.
        const primaryRingResults = await Promise.all(
          samplingPoints.map((point) =>
            tilequeryPoiNearby({
              proximity: {
                latitude: options.proximity!.latitude + point.lat,
                longitude: options.proximity!.longitude + point.lng,
              },
              // 6km radius per sample point covers the gap between neighbours in the 3km-offset
              // ring, so every area gets scanned without relying on a single 10km pull.
              radiusMeters: 6_000,
              limit: 50,
              classes: CURATED_CLASSES,
              // filterrank in streets-v8 is 0 (most prominent) to 5 (least). Default 5 = accept all
              // tiers so international locales (Tokyo, Lima) aren't starved of candidates just
              // because Mapbox rates them lower than US POIs. Downstream filters already de-noise.
              maxFilterRank: 5,
            }).catch(() => []),
          ),
        );
        primaryRingResults.forEach((rows, i) =>
          absorbTileRows(rows, `tilequery-${samplingPoints[i]!.tag}`),
        );

        // Aggressive cost mode: skip unfiltered retries.
      } catch (e) {
        if (__DEV__ || debug) console.warn('[Places] tilequery fallback failed:', e);
      }

      if (__DEV__ || debug) {
        console.warn('[Places] fetchLandmarks Search Box POIs:', allPlaces.length, { searchBoxCalls });
      }

      if (aggressiveCostMode) {
        return await finalizePlaces();
      }

      // Fast path: if Search Box already produced enough landmarks, skip slower query fan-out.
      const fastPathThreshold = Math.max(6, Math.min(requestedLimit, 14));
      if (allPlaces.length >= fastPathThreshold) {
        if (__DEV__ || debug) {
          console.warn('[Places] fetchLandmarks fast-path return from Search Box', {
            places: allPlaces.length,
            threshold: fastPathThreshold,
          });
        }
        return await finalizePlaces();
      }
    }

    if (__DEV__ || debug) {
      console.warn('[Places] fetchLandmarks start', {
        city,
        categories,
        countryCode,
        proximity: options?.proximity || null,
        limit: options?.limit || 20,
      });
    }

    const toProximity = (p?: LocationCoordinates): [number, number] | undefined =>
      p ? [p.longitude, p.latitude] : undefined;

    const isPoiLike = (placeTypes?: string[]) =>
      !!placeTypes?.some((t) => t === 'poi' || t.startsWith('poi'));

    // Search for each term.
    // Key behavior: Mapbox geocoding can match *categories* (e.g. "museum", "restaurant") best when the
    // query is JUST the category word and we constrain via proximity/country.
    // Queries like "museum Cairo" often return 0 POIs (it becomes a name search).
    //
    // Strategy:
    // - Try the bare term first (e.g. "museum") with proximity+country and `types=poi`
    // - If 0 POIs, try city-appended variants
    // - If still 0, retry without `types` (some accounts/regions behave differently)
    const shouldStopEarly = () => allPlaces.length >= earlyStopTarget;
    for (const placeCategory of categories) {
      if (shouldStopEarly()) break;
      for (const term of searchTermsForCategory(placeCategory)) {
      if (shouldStopEarly()) break;
      const baseQueries = [
        term,
        `${term} ${city}`,
        `${city} ${term}`,
      ];
      const fallbackQueries = options?.proximity
        ? []
        : [`${term} in ${city}`, `${term} near ${city}`];
      const queries = [...baseQueries, ...fallbackQueries];

      let picked: Array<{
        id: string;
        name: string;
        coords: LocationCoordinates;
        placeName: string;
        placeTypes?: string[];
        poiCategory?: string;
        relevance?: number;
        isLowQuality: boolean;
      }> = [];

      for (const q of queries) {
        const resultsPoiOnly = await searchPlaces(q, {
          proximity: toProximity(options?.proximity),
          limit: options?.limit || 20,
          types: ['poi'],
          country: countryCodeFilter,
          // Avoid search cache here: we already have a Firestore cache for landmarks,
          // and cached search results can be "poisoned" with missing metadata (e.g. placeTypes),
          // which makes poi filtering drop everything.
          useCache: false,
          language: 'en',
          autocomplete: true,
          fuzzyMatch: true,
        });

        // If we're explicitly querying Mapbox with `types=['poi']`, treat returned results as POIs
        // even if Mapbox omits `place_type` in the response (we've seen this in device logs).
        let poiResults = resultsPoiOnly.length > 0 ? resultsPoiOnly : resultsPoiOnly.filter((r) => isPoiLike(r.placeTypes));
        let totalResults = resultsPoiOnly.length;

        // Fallback: second request without `types`, but keep ONLY Mapbox-typed POIs.
        // Accepting `address`/`neighborhood` here produced generic streets as "landmarks".
        if (poiResults.length === 0) {
          const resultsBroad = await searchPlaces(q, {
            proximity: toProximity(options?.proximity),
            limit: options?.limit || 20,
            country: countryCodeFilter,
            useCache: false,
            language: 'en',
            autocomplete: true,
            fuzzyMatch: true,
          });
          totalResults = resultsBroad.length;
          poiResults = resultsBroad.filter((r) => isPoiLike(r.placeTypes));
        }

        picked = poiResults.map((r, index) => {
          const { label, isLowQuality } = pickLandmarkDisplayLabel(r.featureText, r.placeName);
          const junk = isJunkDiscoveryPoi(label, r.placeName, r.placeTypes, r.relevance);
          return {
            id: `${r.placeName}-${index}`,
            name: label,
            coords: { latitude: r.coordinates[1], longitude: r.coordinates[0] },
            placeName: r.placeName,
            placeTypes: r.placeTypes,
            poiCategory: r.poiCategory,
            relevance: r.relevance,
            isLowQuality: isLowQuality || junk,
          };
        });

        if (__DEV__ || debug) {
          console.warn('[Places] fetchLandmarks result', {
            term,
            query: q,
            results: totalResults,
            poiResults: picked.length,
          });
          if (picked.length === 0 && totalResults > 0) {
            const sample = resultsPoiOnly?.[0] || null;
            console.warn('[Places] fetchLandmarks sample result (for debugging)', sample);
          }
        }

        const usable = picked.filter((p) => !p.isLowQuality);
        if (usable.length > 0) {
          picked = usable;
          break;
        }
      }

      const places = picked
        .filter((p) => !p.isLowQuality)
        .map((p, index) => {
          const popularityScore =
            calculatePopularityScore({
              rating: undefined,
              reviewCount: 0,
              relevance: p.relevance,
            }) + discoveryPopularityBoost(p.name, p.poiCategory);

          return {
            id: `${p.id}-${index}`,
            name: p.name,
            category: placeCategory,
            coordinates: p.coords,
            address: p.placeName,
            rating: undefined,
            description: undefined,
            popularityScore,
            tags: [],
          } as Place;
        })
        .filter((place: Place) => {
          // Filter out duplicates
          return !allPlaces.some(
            (p) =>
              p.coordinates.latitude === place.coordinates.latitude &&
              p.coordinates.longitude === place.coordinates.longitude
          );
        });

      allPlaces.push(...places);
      if (shouldStopEarly()) break;
      }
    }

    if (allPlaces.length === 0 && options?.proximity) {
      const cityShort = city.split(',')[0]?.trim() || city;
      const forwardFallback: { q: string; category: PlaceCategory }[] = [
        { q: 'museum', category: 'museum' },
        { q: 'art museum', category: 'museum' },
        { q: `${cityShort} observation deck`, category: 'attraction' },
        { q: 'historic landmark', category: 'landmark' },
        { q: 'national memorial', category: 'landmark' },
      ];
      for (const { q, category } of forwardFallback) {
        const rows = await searchBoxForwardNearby({
          query: q,
          proximity: options.proximity,
          limit: 10,
          country: countryCodeFilter,
        });
        appendFromSearchBoxRows(rows, category, `fwd-${category}`);
      }
    }

    // Last-resort fallback: run a tiny broad POI search near the resolved proximity.
    // This improves stability in locales where category endpoints are sparse or noisy.
    if (allPlaces.length === 0 && options?.proximity) {
      const broadQueries: Array<{ q: string; category: PlaceCategory }> = [
        { q: 'tourist attraction', category: 'attraction' },
        { q: 'museum', category: 'museum' },
        { q: 'landmark', category: 'landmark' },
      ];
      for (const { q, category } of broadQueries) {
        const rows = await searchPlaces(q, {
          proximity: [options.proximity.longitude, options.proximity.latitude],
          limit: 10,
          types: ['poi'],
          useCache: false,
          language: 'en',
          autocomplete: true,
          fuzzyMatch: true,
        });
        const mapped = rows.map((r) => ({
          coordinates: r.coordinates,
          placeName: r.placeName,
          featureText: r.featureText,
          poiCategory: r.poiCategory,
          placeTypes: r.placeTypes,
          relevance: r.relevance,
        }));
        appendFromSearchBoxRows(mapped, category, `broad-${category}`);
        if (allPlaces.length >= Math.max(6, requestedLimit / 2)) break;
      }
    }

    // FINAL safety net: if we still have zero landmarks AND we were filtering by country, retry
    // the category + broad passes WITHOUT the country filter. Observed for some locales (e.g. JP)
    // where Mapbox Search Box returns empty while the equivalent no-country query returns many POIs.
    if (allPlaces.length === 0 && options?.proximity && countryCodeFilter) {
      if (__DEV__ || debug) {
        console.warn('[Places] fetchLandmarks no-country retry', { city, countryCode: countryCodeFilter });
      }
      const cityShort = city.split(',')[0]?.trim() || city;
      const retryQueries: Array<{ q: string; category: PlaceCategory }> = [
        { q: 'tourist attraction', category: 'attraction' },
        { q: `${cityShort} museum`, category: 'museum' },
        { q: `${cityShort} landmark`, category: 'landmark' },
        { q: `${cityShort} temple`, category: 'landmark' },
        { q: `${cityShort} shrine`, category: 'landmark' },
        { q: `${cityShort} park`, category: 'attraction' },
      ];
      for (const { q, category } of retryQueries) {
        const rows = await searchPlaces(q, {
          proximity: [options.proximity.longitude, options.proximity.latitude],
          limit: 10,
          types: ['poi'],
          useCache: false,
          language: 'en',
          autocomplete: true,
          fuzzyMatch: true,
        });
        const mapped = rows.map((r) => ({
          coordinates: r.coordinates,
          placeName: r.placeName,
          featureText: r.featureText,
          poiCategory: r.poiCategory,
          placeTypes: r.placeTypes,
          relevance: r.relevance,
        }));
        appendFromSearchBoxRows(mapped, category, `nocountry-${category}`);
        if (allPlaces.length >= Math.max(6, requestedLimit / 2)) break;
      }
      // And one Search Box pass without country, if available.
      if (allPlaces.length === 0) {
        try {
          for (const placeCategory of categories) {
            const ids = SEARCH_BOX_CATEGORY_IDS[placeCategory] || [];
            for (const catId of ids) {
              const rows = await searchBoxCategoryNearby(catId, {
                proximity: options.proximity,
                limit: 12,
              });
              appendFromSearchBoxRows(rows, placeCategory, `sb-nocountry-${catId}`);
              if (allPlaces.length >= Math.max(6, requestedLimit / 2)) break;
            }
            if (allPlaces.length >= Math.max(6, requestedLimit / 2)) break;
          }
        } catch (e) {
          if (__DEV__ || debug) console.warn('[Places] no-country Search Box retry failed:', e);
        }
      }
      if (__DEV__ || debug) {
        console.warn('[Places] fetchLandmarks no-country retry result', { places: allPlaces.length });
      }
    }

    return await finalizePlaces();
  } catch (error) {
    console.error('[Places] Error fetching landmarks:', error);

    const cached = await getCachedPlaces(city, { allowExpired: true }, cacheContext);
    // Do not resurrect an empty Firestore row (e.g. after "clear cache") — that masks real failures.
    if (cached && cached.places?.length > 0) {
      return ensurePlaceMetadata(cached.places);
    }

    const seeds = getBaselineLandmarksForCity(city, {
      countryCode,
      limit: Math.min(requestedLimit, 15),
    });
    if (!seeds.length) return [];
    const proximity = options?.proximity;
    const places = seeds
      .map((seed, index) => ({
        id: `baseline-${city.replace(/\s+/g, '-').toLowerCase()}-${index}`,
        name: seed.name,
        category: 'landmark' as const,
        coordinates: seed.coordinates,
        address: seed.address,
        rating: undefined,
        description: undefined,
        popularityScore: Math.max(40, 100 - index * 6),
        tags: ['baseline-seeded'],
      }))
      .sort((a, b) => {
        if (!proximity) return 0;
        return haversineDistance(proximity, a.coordinates) - haversineDistance(proximity, b.coordinates);
      });
    return places.slice(0, requestedLimit);
  }
};

/**
 * Get places near user's current location
 */
export const fetchNearbyPlaces = async (
  categories?: PlaceCategory[],
  radiusMeters: number = 5000
): Promise<Place[]> => {
  try {
    const currentLocation = await getCurrentLocation();
    if (!currentLocation) {
      console.warn('[Places] Cannot get current location for nearby places');
      return [];
    }

    // Use reverse geocoding to get city name
    const { reverseGeocode } = await import('./maps');
    const geocodeResult = await reverseGeocode(currentLocation.longitude, currentLocation.latitude);
    
    if (!geocodeResult) {
      return [];
    }

    // Extract city from place name (typically format: "City, State, Country")
    const city = geocodeResult.placeName.split(',')[0].trim();

    return await fetchLandmarks(city, {
      categories,
      proximity: currentLocation,
      limit: 20,
    });
  } catch (error) {
    console.error('[Places] Error fetching nearby places:', error);
    return [];
  }
};

/**
 * Cache places for a city in Firestore
 */
const parseCachedAt = (value: unknown): Date => {
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date(value as string | number);
};

const stripUndefinedDeepWithCount = <T,>(value: T): { sanitized: T; removedCount: number } => {
  if (Array.isArray(value)) {
    let removedCount = 0;
    const filtered = value.filter((item) => {
      const keep = item !== undefined;
      if (!keep) removedCount += 1;
      return keep;
    });
    const sanitized = filtered
      .map((item) => {
        const nested = stripUndefinedDeepWithCount(item);
        removedCount += nested.removedCount;
        return nested.sanitized;
      }) as T;
    return { sanitized, removedCount };
  }

  if (
    value !== null &&
    typeof value === 'object' &&
    !(value instanceof Date) &&
    !(value instanceof Timestamp)
  ) {
    let removedCount = 0;
    const sanitizedEntries: Array<readonly [string, unknown]> = [];
    Object.entries(value as Record<string, unknown>).forEach(([fieldKey, fieldValue]) => {
      if (fieldValue === undefined) {
        removedCount += 1;
        return;
      }
      const nested = stripUndefinedDeepWithCount(fieldValue);
      removedCount += nested.removedCount;
      sanitizedEntries.push([fieldKey, nested.sanitized] as const);
    });
    return { sanitized: Object.fromEntries(sanitizedEntries) as T, removedCount };
  }

  return { sanitized: value, removedCount: 0 };
};

const isPermissionDeniedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: unknown; message?: unknown };
  if (
    candidate.code === 'permission-denied' ||
    candidate.code === 'missing-or-insufficient-permissions' ||
    candidate.code === 'functions/permission-denied'
  ) {
    return true;
  }

  return (
    typeof candidate.message === 'string' &&
    candidate.message.toLowerCase().includes('insufficient permissions')
  );
};

const isAppActive = (): boolean => AppState.currentState === 'active';

const cachePlaces = async (
  city: string,
  places: Place[],
  context?: LandmarkCacheContext,
): Promise<void> => {
  if (!auth.currentUser || !isAppActive()) {
    return;
  }
  try {
    const cityKey = buildCityCacheKey(city, context);
    const cacheRef = doc(db, 'mapPlacesCache', cityKey);

    const cacheData: CachedPlaces = {
      city,
      places,
      cachedAt: Timestamp.now(),
      schemaVersion: LANDMARK_CACHE_SCHEMA_VERSION,
      cacheKeyVersion: LANDMARK_CACHE_KEY_VERSION,
      countryCode: normalizeCountryCode(context?.countryCode),
      language: normalizeLanguageCode(context?.language),
    };

    const { sanitized: sanitizedCacheData, removedCount } = stripUndefinedDeepWithCount(cacheData);
    if (removedCount > 0) {
      trackSanitizationIntercept({
        scope: 'places_cache',
        reason: 'undefined_fields_removed',
        removedCount,
      });
    }

    await setDoc(cacheRef, sanitizedCacheData, { merge: true });
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    console.error('[Places] Error caching places:', error);
  }
};

/**
 * Get cached places for a city from Firestore
 */
const getCachedPlaces = async (
  city: string,
  options?: { allowExpired?: boolean },
  context?: LandmarkCacheContext,
): Promise<CachedPlaces | null> => {
  try {
    const cityKey = buildCityCacheKey(city, context);
    const legacyCityKey = buildLegacyCityCacheKey(city);
    const cacheRef = doc(db, 'mapPlacesCache', cityKey);
    const snapshot = await getDoc(cacheRef);
    const resolvedSnapshot = snapshot.exists()
      ? snapshot
      : cityKey !== legacyCityKey
        ? await getDoc(doc(db, 'mapPlacesCache', legacyCityKey))
        : snapshot;
    
    if (resolvedSnapshot.exists()) {
      const data = resolvedSnapshot.data();
      const cachedAt = parseCachedAt(data.cachedAt);
      
      // Check if cache is expired
      if (!options?.allowExpired && Date.now() - cachedAt.getTime() > CACHE_FRESH_MS) {
        return null;
      }
      
      return {
        city: data.city,
        places: data.places,
        cachedAt,
        schemaVersion: typeof data.schemaVersion === 'number' ? data.schemaVersion : 0,
        cacheKeyVersion: typeof data.cacheKeyVersion === 'number' ? data.cacheKeyVersion : 0,
        countryCode: typeof data.countryCode === 'string' ? data.countryCode : null,
        language: typeof data.language === 'string' ? data.language : null,
      };
    }

    return null;
  } catch (error) {
    console.error('[Places] Error getting cached places:', error);
    return null;
  }
};

/**
 * Read cached landmarks quickly for immediate map hydration.
 * Can optionally include expired cache entries to avoid blank map while refreshing.
 */
export const getCachedLandmarks = async (
  city: string,
  options?: { allowExpired?: boolean; country?: string; language?: string },
): Promise<Place[]> => {
  const cached = await getCachedPlaces(
    city,
    options,
    { countryCode: options?.country || getCountryCodeFromDestination(city), language: options?.language },
  );
  if (!cached?.places?.length) return [];
  // Respect the landmark cache schema version so old poisoned entries (e.g. v1 results that
  // contained distant outliers) don't leak into the UI during quick-hydrate.
  if ((cached.schemaVersion ?? 0) < LANDMARK_CACHE_SCHEMA_VERSION) {
    if (__DEV__) {
      console.warn('[Places] getCachedLandmarks skipping old-schema cache', {
        city,
        cachedVersion: cached.schemaVersion ?? 0,
        currentVersion: LANDMARK_CACHE_SCHEMA_VERSION,
      });
    }
    return [];
  }
  return ensurePlaceMetadata(cached.places);
};

/**
 * Clear places cache (useful when updating on WiFi)
 */
export const clearPlacesCache = async (city?: string): Promise<void> => {
  if (!auth.currentUser || !isAppActive()) {
    return;
  }
  try {
    if (city) {
      const possibleKeys = [
        buildCityCacheKey(city, { countryCode: getCountryCodeFromDestination(city), language: 'en' }),
        buildLegacyCityCacheKey(city),
      ];
      await Promise.all(
        possibleKeys.map((cityKey) =>
          setDoc(doc(db, 'mapPlacesCache', cityKey), { places: [], cachedAt: Timestamp.now() }, { merge: true }),
        ),
      );
    }
    // Note: For clearing all caches, we'd need to query all documents, which is expensive
    // For now, we'll just update individual cities
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      return;
    }
    console.error('[Places] Error clearing cache:', error);
  }
};

/**
 * Update places cache when WiFi is available
 */
export const updatePlacesCacheOnWifi = async (city: string): Promise<void> => {
  try {
    // Clear old cache
    await clearPlacesCache(city);
    
    // Fetch fresh data
    await fetchLandmarks(city, { limit: 50 });
  } catch (error) {
    console.error('[Places] Error updating cache on WiFi:', error);
  }
};

const CATEGORY_LIMITS: Record<PlaceCategory, number> = {
  restaurant: 6,
  museum: 7,
  attraction: 14,
  viewpoint: 4,
  landmark: 10,
};

const calculatePopularityScore = ({
  rating,
  reviewCount,
  relevance,
}: {
  rating?: number;
  reviewCount?: number;
  relevance?: number;
}): number => {
  const base = rating ? rating * 20 : 40;
  const reviewBoost = reviewCount ? Math.min(30, Math.log(reviewCount + 1) * 10) : 0;
  const relevanceBoost = relevance ? relevance * 10 : 0;
  return Math.round(base + reviewBoost + relevanceBoost);
};

const ensurePlaceMetadata = (places: Place[]): Place[] => {
  if (!places || places.length === 0) {
    return [];
  }

  return places.map((place, index) => ({
    ...place,
    tags: place.tags || [],
    popularityScore:
      place.popularityScore ??
      Math.max(10, (places.length - index) * 2),
  }));
};

const limitPlacesByCategory = (places: Place[], overallLimit: number): Place[] => {
  const buckets: Record<PlaceCategory, Place[]> = {
    restaurant: [],
    museum: [],
    attraction: [],
    viewpoint: [],
    landmark: [],
  };

  places.forEach((place) => {
    buckets[place.category]?.push(place);
  });

  const prioritized: Place[] = [];

  (Object.keys(buckets) as PlaceCategory[]).forEach((category) => {
    const bucket = buckets[category];
    bucket.sort((a, b) => (b.popularityScore ?? 0) - (a.popularityScore ?? 0));
    const cap = Math.min(CATEGORY_LIMITS[category], bucket.length);
    prioritized.push(...bucket.slice(0, cap));
  });

  if (prioritized.length < overallLimit) {
    const remaining = places
      .filter(
        (place) => !prioritized.some((p) => p.id === place.id),
      )
      .sort((a, b) => (b.popularityScore ?? 0) - (a.popularityScore ?? 0));
    prioritized.push(...remaining.slice(0, overallLimit - prioritized.length));
  }

  return prioritized.slice(0, overallLimit);
};

const tagTrendingPlaces = (places: Place[]): Place[] => {
  if (!places.length) return places;

  const sorted = [...places].sort(
    (a, b) => (b.popularityScore ?? 0) - (a.popularityScore ?? 0),
  );
  const trendingCutoff = Math.max(1, Math.floor(sorted.length * 0.2));
  const trendingIds = new Set(sorted.slice(0, trendingCutoff).map((place) => place.id));

  return places.map((place) => {
    const tags = new Set(place.tags || []);
    let trendingReason = place.trendingReason;

    if (trendingIds.has(place.id)) {
      tags.add('trending');
      if (!trendingReason) {
        trendingReason = buildTrendingReason(place);
      }
    }

    if ((place.rating ?? 0) >= 4.5) {
      tags.add('top-rated');
    }

    return {
      ...place,
      tags: Array.from(tags),
      trendingReason,
    };
  });
};

const buildTrendingReason = (place: Place): string => {
  if (place.rating && place.rating >= 4.5) {
    return 'Loved by travelers (4.5★+)';
  }

  if (place.description) {
    return place.description;
  }

  return 'Popular with Tripvio travelers';
};

